export type ActiveContextState = {
  organizationId: string;
  locationId?: string | null;
  organizationName?: string;
  locationName?: string | null;
  locationCode?: string | null;
  syrveConnectionId?: string | null;
  syrveConnectionName?: string | null;
};

const ACTIVE_CONTEXT_STORAGE_KEY = 'activeContext';
const LEGACY_ORGANIZATION_KEY = 'workspace.organizationId';
const LEGACY_LOCATION_KEY = 'workspace.locationId';
const ACTIVE_CONTEXT_EVENT = 'active-context-updated';

type ActiveContextListener = () => void;
export type ActiveSyrveConnectionCandidate = {
  id: string;
  name?: string | null;
  isDefault?: boolean | null;
};

let cachedRaw: string | null = null;
let cachedLegacySignature: string | null = null;
let cachedContext: ActiveContextState | null = null;

function emitContextChanged() {
  window.dispatchEvent(new Event(ACTIVE_CONTEXT_EVENT));
}

function normalizeContext(context: ActiveContextState): ActiveContextState {
  return {
    organizationId: context.organizationId,
    locationId: context.locationId ?? null,
    organizationName: context.organizationName,
    locationName: context.locationName ?? null,
    locationCode: context.locationCode ?? null,
    syrveConnectionId: context.syrveConnectionId ?? null,
    syrveConnectionName: context.syrveConnectionName ?? null,
  };
}

function readLegacyContext(): ActiveContextState | null {
  const organizationId = localStorage.getItem(LEGACY_ORGANIZATION_KEY);
  if (!organizationId) {
    return null;
  }

  const locationId = localStorage.getItem(LEGACY_LOCATION_KEY);
  return {
    organizationId,
    locationId: locationId || null,
    syrveConnectionId: null,
    syrveConnectionName: null,
  };
}

function getLegacySignature() {
  return [
    localStorage.getItem(LEGACY_ORGANIZATION_KEY) ?? '',
    localStorage.getItem(LEGACY_LOCATION_KEY) ?? '',
  ].join('\u0000');
}

export function getActiveContext(): ActiveContextState | null {
  const raw = localStorage.getItem(ACTIVE_CONTEXT_STORAGE_KEY);
  if (!raw) {
    const legacySignature = getLegacySignature();
    if (cachedRaw === null && cachedLegacySignature === legacySignature) {
      return cachedContext;
    }

    cachedRaw = null;
    cachedLegacySignature = legacySignature;
    cachedContext = readLegacyContext();
    return cachedContext;
  }

  if (raw === cachedRaw) {
    return cachedContext;
  }

  try {
    const parsed = JSON.parse(raw) as ActiveContextState;
    if (!parsed.organizationId) {
      cachedRaw = null;
      cachedContext = readLegacyContext();
      return cachedContext;
    }
    cachedRaw = raw;
    cachedLegacySignature = null;
    cachedContext = normalizeContext(parsed);
    return cachedContext;
  } catch {
    cachedRaw = null;
    cachedLegacySignature = getLegacySignature();
    cachedContext = readLegacyContext();
    return cachedContext;
  }
}

export function setActiveContext(context: ActiveContextState) {
  const normalized = normalizeContext(context);

  const serialized = JSON.stringify(normalized);
  localStorage.setItem(ACTIVE_CONTEXT_STORAGE_KEY, serialized);
  localStorage.setItem(LEGACY_ORGANIZATION_KEY, normalized.organizationId);
  if (normalized.locationId) {
    localStorage.setItem(LEGACY_LOCATION_KEY, normalized.locationId);
  } else {
    localStorage.removeItem(LEGACY_LOCATION_KEY);
  }
  cachedRaw = serialized;
  cachedLegacySignature = null;
  cachedContext = normalized;
  emitContextChanged();
}

export function setActiveSyrveConnection(connection: ActiveSyrveConnectionCandidate) {
  const current = getActiveContext();
  if (!current?.organizationId) {
    return null;
  }

  const next = normalizeContext({
    ...current,
    syrveConnectionId: connection.id,
    syrveConnectionName: connection.name ?? null,
  });
  setActiveContext(next);
  return next;
}

export function clearActiveSyrveConnection() {
  const current = getActiveContext();
  if (!current?.organizationId) {
    return null;
  }

  const next = normalizeContext({
    ...current,
    syrveConnectionId: null,
    syrveConnectionName: null,
  });
  setActiveContext(next);
  return next;
}

export function resolvePreferredSyrveConnection(
  connections: ActiveSyrveConnectionCandidate[],
  context: ActiveContextState | null = getActiveContext(),
) {
  if (context?.syrveConnectionId) {
    const remembered = connections.find((connection) => connection.id === context.syrveConnectionId);
    if (remembered) {
      return remembered;
    }
  }

  return connections.find((connection) => connection.isDefault) ?? connections[0] ?? null;
}

export function syncActiveSyrveConnection(connections: ActiveSyrveConnectionCandidate[]) {
  const current = getActiveContext();
  if (!current?.organizationId) {
    return null;
  }

  const resolved = resolvePreferredSyrveConnection(connections, current);
  if (!resolved) {
    if (current.syrveConnectionId || current.syrveConnectionName) {
      clearActiveSyrveConnection();
    }
    return null;
  }

  if (
    current.syrveConnectionId !== resolved.id
    || (resolved.name !== undefined && current.syrveConnectionName !== (resolved.name ?? null))
  ) {
    setActiveSyrveConnection(resolved);
  }

  return resolved;
}

export function clearActiveContext() {
  localStorage.removeItem(ACTIVE_CONTEXT_STORAGE_KEY);
  localStorage.removeItem(LEGACY_ORGANIZATION_KEY);
  localStorage.removeItem(LEGACY_LOCATION_KEY);
  cachedRaw = null;
  cachedLegacySignature = null;
  cachedContext = null;
  emitContextChanged();
}

export function subscribeToActiveContext(listener: ActiveContextListener) {
  const handleStorage = (event: StorageEvent) => {
    if (
      event.key === ACTIVE_CONTEXT_STORAGE_KEY
      || event.key === LEGACY_ORGANIZATION_KEY
      || event.key === LEGACY_LOCATION_KEY
    ) {
      listener();
    }
  };
  window.addEventListener('storage', handleStorage);
  window.addEventListener(ACTIVE_CONTEXT_EVENT, listener);
  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener(ACTIVE_CONTEXT_EVENT, listener);
  };
}
